import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { Council, Society } from 'types';

import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import { getCouncilAttendanceText } from '../utils/getCouncilAttendanceText';
import { getDirectorsFromParticipants } from '../utils/getDirectorsFromParticipants';
import { CouncilMembersParticipantsRow } from './CouncilMembersParticipantsRow';

type CouncilMembersParticipantsCardProps = {
  society: Society;
  council: Council;
  isAdminView: boolean;
};

const CouncilMembersParticipantsCard: FC<
  CouncilMembersParticipantsCardProps
> = ({ society, council, isAdminView }) => {
  const { t } = useTranslate();

  const directors = useMemo(
    () =>
      getDirectorsFromParticipants(council?.participants, society?.directors),
    [council, society]
  );

  const isAllAttendance = useMemo(
    () => council?.participants?.every((participant) => participant.assists),
    [council]
  );

  const attendanceText = getCouncilAttendanceText(council);

  const handleChangeAllAttendance = async () => {
    const updatedParticipants = council?.participants?.map((participant) => ({
      ...participant,
      assists: !isAllAttendance,
      isAbsent: isAllAttendance,
    }));

    const councilUpdateData = {
      _id: council._id,
      participants: updatedParticipants,
    };

    await store.dispatch(updateCouncil(councilUpdateData));
  };

  return (
    <section className="council-member-basic-info-card col px-0">
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title title mb-1">{t('CouncilMembers')}</h5>
          <div className="d-flex align-items-center justify-content-between">
            <span>
              {t('CouncilMembersAttendance')} <b>{attendanceText}</b>
            </span>

            {isAdminView && (
              <div className="d-flex align-items-center pr-3 mr-1">
                <span className="text-link">{t('AttendAll')}</span>
                <div className="custom-control custom-control-sm custom-checkbox ml-2">
                  <input
                    type="checkbox"
                    id="participant-all"
                    className="custom-control-input ml-2"
                    checked={isAllAttendance}
                    onChange={handleChangeAllAttendance}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="participant-all"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card rounded-xl">
        <ul className="data-list is-compact">
          {directors?.map((director, index) => (
            <CouncilMembersParticipantsRow
              key={director._id}
              director={director}
              society={society}
              council={council}
              index={index}
              isAdminView={isAdminView}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};

export default CouncilMembersParticipantsCard;
